/**
 * Wordings of «Учитель» v3 — family «theme» (docs/TEACHING-V3.md §2.1, §2.9, §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * Pools of this file:
 *  - `v3.theme.<strategyId>` (stages 3–5) — the opening's name + its idea in ONE sentence, no move, no square. For White
 *    it may come after the opponent's first reply, so nothing here claims a move that was (or was not) played. The
 *    openers differ from card to card («Сегодня играем», «Наша тема», «Разыграем», «В …», and one of «Знакомься»,
 *    «Нас ждёт», «Выбираем», «Попробуем» or the name itself), so the start of a game does not sound like a form;
 *  - `v3.theme.named.<strategyId>` (stages 1–5) — the name as a reward once the line was played («Смотри-ка, у нас
 *    получилась «Итальянская партия»!»). No claim about a concrete move either;
 *  - `v3.theme.family.<family>` (banded) — the game's idea without the name, said before the first move (or after the
 *    opponent's first move for Black): a PLAN, never «мы уже заняли центр», no «уже / снова / дальше», and no claim
 *    about the opponent's first move («соперник строит центр» is false after 1.Nf3; «займём центр раньше соперника»
 *    is false for Black after 1.e4). Fortress wordings never say «сначала пешки, потом фигуры» (the London, the
 *    Caro-Kann and the Slav bring a bishop out before the pawn wall);
 *  - `v3.theme.remind.<family>` — a reminder in the opening or while a card goal is open, i.e. also deep in the
 *    middlegame and in the endgame, glued in front of an advice: speaks of the plan or a norm, never of the position
 *    («король ждёт домик» is false after an early castle, «фигуры выходят / каждый ход — новая фигура / кто ещё
 *    спит» are false in the middlegame, «наша крепость держится» after the wall broke), and never asks to wait
 *    («удар подождёт») — the advice right after it may be that very strike;
 *  - `v3.theme.left` (3–5) — the opponent left the card's line: the plan stays;
 *  - `v3.phase.middlegame` / `v3.phase.endgame` — the chapter lines, once per game; no «вышел из домика» (the king may
 *    never have castled), no «фигуры вышли / развиты» (the middlegame starts by the move number or by early trades,
 *    with pieces possibly at home), the endgame king «может» fight (a queen may still be on the board);
 *  - `v3.recall.<takeaway>` — a later game recalls a takeaway and promises to check it today; ONE sentence (the pools
 *    have no maxSentences), about «мы», never blaming (no «забыл», «снова», «опять»). Time-neutral («недавно», «наш
 *    урок»), never «в прошлый раз / прошлой партии»: core `recallKeyOf` may take the takeaway of the game before last.
 *    Said before our first move, right after the announcement. A theme recall says only that the plan worked, never
 *    more («охота удалась» would claim the pawn was won; «когда мы отдали пешку» is false if the gambit was declined).
 *
 * Stage words (docs §2.10, lint STAGE_TERMS): «развитие», «вилка», «слабая пешка у короля», «гамбит» as a word — from 2;
 * «фланг», «диагональ», «вертикаль», «открытая линия», «связка», «контрудар» — from 3; «эндшпиль», «проходная» — 5 only.
 * At stages 1–2 it is «сторона короля / ферзя», «свободная дорога без пешек», «пешка рядом с королём соперника».
 * Names of openings are in «ёлочках» and spelled as `titleRu` (the badge «Тема: …» shows the same words).
 * The family pools avoid the sentences of `v3.why.*` / `v3.themeTail.*` (plan.ru.ts): the same game hears both.
 */
import type { LessonWordings } from '../types.ts';

const YOUNG = [1, 2] as const;
const OLD = [3, 5] as const;
const FROM2 = [2, 5] as const;
const ONLY1 = [1, 1] as const;
const ONLY5 = [5, 5] as const;

export const WORDINGS: LessonWordings = {
  // ───────────────────────── v3.theme.<id>: the name + the idea, stages 3–5 ─────────────────────────
  'v3.theme.italian': [
    { t: 'Сегодня играем «Итальянскую партию» — быстро выводим фигуры и целимся в слабую пешку у короля.' },
    { t: 'Наша тема — «Итальянская партия»: слон целится в слабую пешку рядом с королём.' },
    { t: 'Разыграем «Итальянскую партию»: пешки держат центр, а фигуры спешат в бой.' },
    { t: 'В «Итальянской партии» фигуры выходят быстро, а слон смотрит в сторону чужого короля.' },
    { t: 'Знакомься: «Итальянская партия» — старинное начало с прицелом на пешку у короля.' },
  ],
  'v3.theme.four-knights': [
    { t: 'Сегодня играем «Партию четырёх коней»: оба коня в бой, потом слоны и рокировка.' },
    { t: 'Наша тема — «Партия четырёх коней»: сначала выводим обоих коней к центру.' },
    { t: 'Разыграем «Партию четырёх коней» — спокойно выводим все фигуры и прячем короля.' },
    { t: 'В «Партии четырёх коней» все кони выходят раньше слонов.' },
    { t: 'Нас ждёт «Партия четырёх коней»: кони в бой, а слон давит на коня соперника.' },
  ],
  'v3.theme.london': [
    { t: 'Сегодня играем «Лондонскую систему»: сначала слон, потом крепость из пешек.' },
    { t: 'Наша тема — «Лондонская система»: слон выходит на свободу, а пешки за ним строят крепость.' },
    { t: 'Разыграем «Лондонскую систему» — крепость из пешек в центре и спокойное развитие.' },
    { t: 'В «Лондонской системе» строим крепость из пешек, а конь потом прыгает в центр.' },
    { t: 'Выбираем «Лондонскую систему»: слон на свободу, пешки стеной, король в домик.' },
  ],
  'v3.theme.bishops-opening': [
    { t: 'Сегодня играем «Дебют слона»: слон выходит первым и сразу смотрит на слабую пешку.' },
    { t: 'Наша тема — «Дебют слона»: слон целится в слабую пешку рядом с королём соперника.' },
    { t: 'Разыграем «Дебют слона» — пешки спокойно держат центр, а слон уже на охоте.' },
    { t: 'В «Дебюте слона» слон выходит даже раньше коней — так ему не терпится в бой.' },
    { t: 'Попробуем «Дебют слона»: пешка в центр, слон вперёд, потом конь и рокировка.' },
  ],
  'v3.theme.colle': [
    { t: 'Сегодня играем «Систему Колле»: строим крепость из пешек и готовим удар в центре.' },
    { t: 'Наша тема — «Система Колле»: пешки стоят стеной, а потом одна бьёт в центр.' },
    { t: 'Разыграем «Систему Колле» — крепкие пешки, слон смотрит на короля, потом удар в центре.' },
    { t: 'В «Системе Колле» фигуры выходят за стеной пешек, а король уходит в домик.' },
    { t: '«Система Колле» похожа на пружину: сначала тихо сжимается, а потом бьёт пешкой в центр.' },
  ],
  'v3.theme.scotch': [
    { t: 'Сегодня играем «Шотландскую партию» — сразу бьёмся за центр пешкой.' },
    { t: 'Наша тема — «Шотландская партия»: не ждём, а сразу открываем центр.' },
    { t: 'Разыграем «Шотландскую партию»: удар пешкой в центре, потом фигуры выходят на открытые линии.' },
    { t: 'В «Шотландской партии» центр открывается рано, и фигуры спешат в бой.' },
    { t: 'Нас ждёт «Шотландская партия»: смело бьём в центр и быстро выводим фигуры.' },
  ],
  'v3.theme.vienna': [
    { t: 'Сегодня играем «Венскую партию»: конь выходит раньше слона и крепко держит центр.' },
    { t: 'Наша тема — «Венская партия»: конь сторожит пешку в центре, а слон выходит следом.' },
    { t: 'Разыграем «Венскую партию» — сначала конь к центру, потом слон и рокировка.' },
    { t: 'В «Венской партии» центр держат пешка и конь, который её защищает.' },
    { t: 'Знакомься: «Венская партия» — сначала крепкий центр, а потом слон целится в слабую пешку.' },
  ],
  'v3.theme.spanish': [
    { t: 'Сегодня играем «Испанскую партию»: слон давит на коня, который защищает центр.' },
    { t: 'Наша тема — «Испанская партия»: давим на коня соперника, чтобы ослабить его центр.' },
    { t: 'Разыграем «Испанскую партию»: фигуры выходят, ладья помогает центру, потом удар пешкой.' },
    { t: 'В «Испанской партии» слон с первых ходов мешает коню соперника держать центр.' },
    { t: '«Испанская партия» — игра для терпеливых: спокойно выводим фигуры и давим на центр.' },
  ],
  'v3.theme.queens-gambit': [
    { t: 'Сегодня играем «Ферзевый гамбит»: предлагаем пешку, чтобы забрать центр себе.' },
    { t: 'Наша тема — «Ферзевый гамбит»: пешка сбоку бьёт по центру соперника.' },
    { t: 'Разыграем «Ферзевый гамбит»: предложим пешку сбоку, а сами займём центр.' },
    { t: 'В «Ферзевом гамбите» пешка сбоку вызывает на спор пешку соперника в центре.' },
    { t: 'Попробуем «Ферзевый гамбит»: пешки спорят за центр, а фигуры спешат следом.' },
  ],
  'v3.theme.open-game': [
    { t: 'Сегодня играем «Открытую игру»: отвечаем пешкой в центр и выводим все фигуры.' },
    { t: 'Наша тема — «Открытая игра»: пешка в центр, конь её защищает, дальше слон и рокировка.' },
    { t: 'Разыграем «Открытую игру» — встречаем пешку соперника своей пешкой прямо в центре.' },
    { t: 'В «Открытой игре» мы держим центр пешкой и быстро будим все фигуры.' },
    { t: 'Выбираем «Открытую игру» — честный бой за центр и рокировка пораньше.' },
  ],
  'v3.theme.two-knights': [
    { t: 'Сегодня играем «Защиту двух коней»: второй конь сразу нападает на пешку в центре.' },
    { t: 'Наша тема — «Защита двух коней»: оба коня выходят и давят на центр.' },
    { t: 'Разыграем «Защиту двух коней» — кони прыгают к центру, потом слон и рокировка.' },
    { t: 'В «Защите двух коней» кони смело спорят за центр с первых ходов.' },
    { t: '«Защита двух коней» — это два коня, как два стража у центра.' },
  ],
  'v3.theme.french': [
    { t: 'Сегодня играем «Французскую защиту»: строим цепочку пешек и бьём по цепочке соперника сбоку.' },
    { t: 'Наша тема — «Французская защита»: пешки стоят цепочкой, а пешка сбоку ломает чужой центр.' },
    { t: 'Разыграем «Французскую защиту» — крепкие пешки в центре и удар сбоку по чужим пешкам.' },
    { t: 'Во «Французской защите» мы давим конём и ферзём на пешку в центре соперника.' },
    { t: 'Выбираем «Французскую защиту»: спокойная цепочка пешек, а потом смелый удар сбоку.' },
  ],
  'v3.theme.scandinavian': [
    { t: 'Сегодня играем «Скандинавскую защиту»: сразу бьём по центру и быстро выводим фигуры.' },
    { t: 'Наша тема — «Скандинавская защита»: с первого хода пешка бьёт по пешке соперника в центре.' },
    { t: 'Разыграем «Скандинавскую защиту» — удар по центру, кони в бой, слон связывает коня.' },
    { t: 'В «Скандинавской защите» соперник не успевает спокойно построить центр.' },
    { t: 'Знакомься: «Скандинавская защита» — смелый удар в центре и быстрые фигуры.' },
  ],
  'v3.theme.sicilian': [
    { t: 'Сегодня играем «Сицилианскую защиту»: спорим за центр сбоку, а слон-дракон стреляет издалека.' },
    { t: 'Наша тема — «Сицилианская защита»: пешка сбоку спорит за центр.' },
    { t: 'Разыграем «Сицилианскую защиту» — наш слон-дракон будет стрелять по длинной диагонали.' },
    { t: 'В «Сицилианской защите» слона на длинной диагонали зовут драконом — он стреляет через всю доску.' },
    { t: 'Нас ждёт «Сицилианская защита»: удар сбоку по центру, дракон на диагонали, король в домике.' },
  ],
  'v3.theme.caro-kann': [
    { t: 'Сегодня играем «Защиту Каро-Канн»: строим крепкий центр, а слон выходит на свободу.' },
    { t: 'Наша тема — «Защита Каро-Канн»: слон выходит раньше, чем пешки закроют ему дорогу.' },
    { t: 'Разыграем «Защиту Каро-Канн»: крепкие пешки, свободный слон и король в домике.' },
    { t: 'В «Защите Каро-Канн» одна пешка готовит путь другой, и центр становится крепким.' },
    { t: 'Попробуем «Защиту Каро-Канн» — спокойное начало, где пешки стоят как крепость.' },
  ],
  'v3.theme.orthodox': [
    { t: 'Сегодня играем «Ортодоксальную защиту»: крепко держим пешку в центре и спокойно выводим фигуры.' },
    { t: 'Наша тема — «Ортодоксальная защита»: пешка в центре стоит крепко, её поддерживают соседки.' },
    { t: 'Разыграем «Ортодоксальную защиту» — спокойное развитие за крепкой стеной пешек.' },
    { t: 'В «Ортодоксальной защите» держим центр крепко, а потом освобождаем слона.' },
    { t: 'Нас ждёт «Ортодоксальная защита»: никуда не спешим, держим центр и прячем короля.' },
  ],
  'v3.theme.slav': [
    { t: 'Сегодня играем «Славянскую защиту»: пешка сбоку поддерживает центр, а слон выходит заранее.' },
    { t: 'Наша тема — «Славянская защита»: крепкий центр и слон, который не сидит взаперти.' },
    { t: 'Разыграем «Славянскую защиту»: две пешки держат центр, а слон спешит на свободу.' },
    { t: 'В «Славянской защите» мы строим крепкий центр и спокойно прячем короля.' },
    { t: 'Выбираем «Славянскую защиту» — надёжные пешки и быстрый слон.' },
  ],
  'v3.theme.dutch-stonewall': [
    { t: 'Сегодня играем «Голландскую защиту»: строим каменную стену из пешек и идём на короля.' },
    { t: 'Наша тема — «Голландская защита»: конь прыгает в центр, а ферзь спешит к королю соперника.' },
    { t: 'Разыграем «Голландскую защиту» — пешки стоят каменной стеной, а фигуры собираются у чужого короля.' },
    { t: 'В «Голландской защите» пешки строят каменную стену, а слон смотрит на королевский фланг.' },
    { t: 'Знакомься: «Голландская защита» — крепкая стена в центре и атака на короля соперника.' },
  ],
  'v3.theme.kings-indian': [
    { t: 'Сегодня играем «Староиндийскую защиту»: прячем короля за слоном и ломаем центр пешкой.' },
    { t: 'Наша тема — «Староиндийская защита»: слон на длинной диагонали, король за ним в домике.' },
    { t: 'Разыграем «Староиндийскую защиту» — сначала прячем короля, потом пешки идут в атаку.' },
    { t: 'В «Староиндийской защите» пускаем соперника в центр, а потом бьём по нему пешкой.' },
    { t: 'Нас ждёт «Староиндийская защита»: король в крепости, а пешки на королевском фланге идут вперёд.' },
  ],
  'v3.theme.classic-development': [
    { t: 'Сегодня играем «Классическое развитие»: пешка в центр, фигуры в игру, король в домик.' },
    { t: 'Наша тема — «Классическое развитие»: каждым ходом выводим новую фигуру.' },
    { t: 'Разыграем «Классическое развитие» — простые правила: центр, фигуры, рокировка.' },
    { t: 'В «Классическом развитии» нет хитростей: мы спокойно выводим фигуры и прячем короля.' },
    { t: 'Нас ждёт «Классическое развитие»: крепкий центр, фигуры в бою, потом пешки сбоку идут вперёд.' },
  ],
  'v3.theme.kings-indian-setup': [
    { t: 'Сегодня играем «Староиндийское построение»: слон на длинной диагонали охраняет нашего короля.' },
    { t: 'Наша тема — «Староиндийское построение»: сначала крепость для короля, потом бой за центр.' },
    { t: 'Разыграем «Староиндийское построение» — король прячется за слоном, как за стражником.' },
    { t: 'В «Староиндийском построении» пешка делает шаг вперёд, и слон встаёт на длинную дорогу.' },
    { t: 'Попробуем «Староиндийское построение»: король в домике, а пешки спокойно держат центр.' },
  ],
  'v3.theme.queens-indian-setup': [
    { t: 'Сегодня играем «Новоиндийское построение»: слон смотрит по длинной диагонали прямо в центр.' },
    { t: 'Наша тема — «Новоиндийское построение»: пешка открывает дорогу слону, и он держит центр издалека.' },
    { t: 'Разыграем «Новоиндийское построение» — слон и конь вместе сторожат центр.' },
    { t: 'В «Новоиндийском построении» центр держат не только пешки, но и фигуры издалека.' },
    { t: 'Попробуем «Новоиндийское построение»: слон на длинной дороге, король в домике.' },
  ],
  'v3.theme.botvinnik-system': [
    { t: 'Сегодня играем «Систему Ботвинника»: две пешки крепко держат центр с двух сторон.' },
    { t: 'Наша тема — «Система Ботвинника»: пешки стоят крепко, а слон смотрит по длинной диагонали.' },
    { t: 'Разыграем «Систему Ботвинника» — так играл чемпион мира: пешки впереди, фигуры за ними.' },
    { t: 'В «Системе Ботвинника» слон на длинной дороге помогает пешкам держать центр.' },
    { t: 'Выбираем «Систему Ботвинника»: крепкий центр, король в домике, а потом пешка идёт вперёд.' },
  ],
  'v3.theme.reversed-sicilian': [
    { t: 'Сегодня играем «Сицилианскую наоборот»: сразу ставим пешку в центр и выводим коней.' },
    { t: 'Наша тема — «Сицилианская наоборот»: пешка встаёт в центр, а кони спешат ей на помощь.' },
    { t: 'Разыграем «Сицилианскую наоборот» — пешка в центр, кони в бой, потом удар второй пешкой.' },
    { t: 'В «Сицилианской наоборот» мы занимаем центр пешкой и быстро будим коней.' },
    { t: 'Знакомься: «Сицилианская наоборот» — смелая пешка в центре и два коня рядом.' },
  ],

  // ───────────────────────── v3.theme.named.<id>: the name as a reward, stages 1–5 ─────────────────────────
  'v3.theme.named.italian': [
    { t: 'Смотри-ка, у нас получилась «Итальянская партия»!' },
    { t: 'Наши ходы сложились в «Итальянскую партию» — ей уже несколько сотен лет!' },
    { t: 'Шахматисты называют такое начало «Итальянской партией».' },
  ],
  'v3.theme.named.four-knights': [
    { t: 'Ого, да это же «Партия четырёх коней»!' },
    { t: 'У этого начала есть имя — «Партия четырёх коней».' },
    { t: 'Запомни имя нашего начала: «Партия четырёх коней».' },
  ],
  'v3.theme.named.london': [
    { t: 'Ура, у нас получилась «Лондонская система»!' },
    { t: 'Наши ходы сложились в «Лондонскую систему»!' },
    { t: 'У такого начала красивое имя — «Лондонская система».' },
  ],
  'v3.theme.named.bishops-opening': [
    { t: 'Смотри-ка, у нас получился «Дебют слона»!' },
    { t: 'Шахматисты называют такое начало «Дебютом слона».' },
    { t: 'Вот и получился настоящий «Дебют слона»!' },
  ],
  'v3.theme.named.colle': [
    { t: 'Ого, да это же «Система Колле»!' },
    { t: 'У нашего начала есть имя — «Система Колле», в честь шахматиста Колле.' },
    { t: 'Наши ходы сложились в «Систему Колле»!' },
  ],
  'v3.theme.named.scotch': [
    { t: 'Смотри-ка, у нас получилась «Шотландская партия»!' },
    { t: 'Запомни имя нашего начала: «Шотландская партия».' },
    { t: 'Такое смелое начало называют «Шотландской партией».' },
  ],
  'v3.theme.named.vienna': [
    { t: 'Ура, у нас получилась «Венская партия»!' },
    { t: 'Шахматисты называют такое начало «Венской партией».' },
    { t: 'Вот и получилась настоящая «Венская партия»!' },
  ],
  'v3.theme.named.spanish': [
    { t: 'Смотри-ка, у нас получилась «Испанская партия»!' },
    { t: 'Наши ходы сложились в «Испанскую партию» — её играют уже больше четырёхсот лет!' },
    { t: 'У этого начала есть имя — «Испанская партия».' },
  ],
  'v3.theme.named.queens-gambit': [
    { t: 'Ого, да это же «Ферзевый гамбит»!' },
    { t: 'У нас получился «Ферзевый гамбит» — гамбитом называют начало, где предлагают пешку.' },
    { t: 'Запомни имя нашего начала: «Ферзевый гамбит».' },
  ],
  'v3.theme.named.open-game': [
    { t: 'Смотри-ка, у нас получилась «Открытая игра»!' },
    { t: 'Такое начало называют «Открытой игрой» — пешки встретились прямо в центре.' },
    { t: 'Наши ходы сложились в «Открытую игру»!' },
  ],
  'v3.theme.named.two-knights': [
    { t: 'Ого, да это же «Защита двух коней»!' },
    { t: 'Так и зовут наше начало — «Защита двух коней».' },
    { t: 'Шахматисты называют такое начало «Защитой двух коней».' },
  ],
  'v3.theme.named.french': [
    { t: 'Смотри-ка, у нас получилась «Французская защита»!' },
    { t: 'У этого начала есть имя — «Французская защита».' },
    { t: 'Вот и получилась настоящая «Французская защита»!' },
  ],
  'v3.theme.named.scandinavian': [
    { t: 'Ура, у нас получилась «Скандинавская защита»!' },
    { t: 'Шахматисты называют такое начало «Скандинавской защитой».' },
    { t: 'Наши ходы сложились в «Скандинавскую защиту»!' },
  ],
  'v3.theme.named.sicilian': [
    { t: 'Ого, да это же «Сицилианская защита»!' },
    { t: 'Наши ходы сложились в «Сицилианскую защиту» — её очень любят чемпионы!' },
    { t: 'Запомни имя нашего начала: «Сицилианская защита».' },
  ],
  'v3.theme.named.caro-kann': [
    { t: 'Смотри-ка, у нас получилась «Защита Каро-Канн»!' },
    { t: 'Такое начало назвали по фамилиям двух шахматистов — «Защита Каро-Канн».' },
    { t: 'У этого начала есть имя — «Защита Каро-Канн».' },
  ],
  'v3.theme.named.orthodox': [
    { t: 'Ура, у нас получилась «Ортодоксальная защита»!' },
    { t: 'Шахматисты называют такое начало «Ортодоксальной защитой».' },
    { t: 'Вот и получилась настоящая «Ортодоксальная защита»!' },
  ],
  'v3.theme.named.slav': [
    { t: 'Смотри-ка, у нас получилась «Славянская защита»!' },
    { t: 'Наши ходы сложились в «Славянскую защиту»!' },
    { t: 'Запомни имя нашего начала: «Славянская защита».' },
  ],
  'v3.theme.named.dutch-stonewall': [
    { t: 'Ого, да это же «Голландская защита»!' },
    { t: 'У этого начала есть имя — «Голландская защита».' },
    { t: 'Шахматисты называют такое начало «Голландской защитой».' },
  ],
  'v3.theme.named.kings-indian': [
    { t: 'Смотри-ка, у нас получилась «Староиндийская защита»!' },
    { t: 'Наши ходы сложились в «Староиндийскую защиту»!' },
    { t: 'Вот и получилась настоящая «Староиндийская защита»!' },
  ],
  // «развитие» is a stage-2 word: at stage 1 the card is named without it
  'v3.theme.named.classic-development': [
    { t: 'Смотри-ка, у нас получилось «Классическое развитие»!', stages: FROM2 },
    { t: 'Шахматисты называют такое начало «Классическим развитием».', stages: FROM2 },
    { t: 'Запомни имя нашего начала: «Классическое развитие».', stages: FROM2 },
    { t: 'Ура, у нас получилось классическое начало партии!', stages: ONLY1 },
    { t: 'Пешка в центр, фигуры в игру — вот и получилось классическое начало!', stages: ONLY1 },
    { t: 'Так начинают партию настоящие шахматисты — и у нас получилось!', stages: ONLY1 },
  ],
  'v3.theme.named.kings-indian-setup': [
    { t: 'Ура, у нас получилось «Староиндийское построение»!' },
    { t: 'У этого начала есть имя — «Староиндийское построение».' },
    { t: 'Наши ходы сложились в «Староиндийское построение»!' },
  ],
  'v3.theme.named.queens-indian-setup': [
    { t: 'Ого, да это же «Новоиндийское построение»!' },
    { t: 'Шахматисты называют такое начало «Новоиндийским построением».' },
    { t: 'Вот и получилось настоящее «Новоиндийское построение»!' },
  ],
  'v3.theme.named.botvinnik-system': [
    { t: 'Смотри-ка, у нас получилась «Система Ботвинника»!' },
    { t: 'Наше начало называется «Система Ботвинника» — в честь чемпиона мира.' },
    { t: 'Запомни имя нашего начала: «Система Ботвинника».' },
  ],
  'v3.theme.named.reversed-sicilian': [
    { t: 'Ого, да это же «Сицилианская наоборот»!' },
    { t: 'У этого начала смешное имя — «Сицилианская наоборот».' },
    { t: 'Наши ходы сложились в «Сицилианскую наоборот»!' },
  ],

  // ───────────────────────── v3.theme.family.<family>: the idea without the name (banded) ─────────────────────────
  // Said before OUR first move (White before move 1, Black after the opponent's first move): a plan for the game,
  // never «уже / снова / дальше», never a claim about the position («соперник строит центр», «займём раньше соперника»
  // are false after 1.Nf3 or for Black after 1.e4).
  'v3.theme.family.center': [
    { t: 'Сегодня главное — центр: занимаем его пешками и фигурами.', stages: YOUNG },
    { t: 'Вот эти четыре клетки посередине — центр, сегодня постараемся его занять.', stages: YOUNG },
    { t: 'Наш план на эту партию — забрать центр доски себе.', stages: YOUNG },
    { t: 'Пешки идут в середину, а фигуры им помогают — так сегодня играем за центр.', stages: YOUNG },
    { t: 'Давай сделаем центр нашим — это самое важное место на доске!', stages: YOUNG },
    { t: 'Главная цель партии — центр: поставим туда пешки и фигуры.', stages: YOUNG },
    { t: 'Из центра конь достаёт до восьми клеток, а из угла — до двух: займём центр!', stages: YOUNG },
    { t: 'Тема партии — центр: занимаем и держим его пешками и фигурами.', stages: OLD },
    { t: 'Сегодня наш план — сильный центр: пешки впереди, фигуры поддерживают.', stages: OLD },
    { t: 'Вот эти клетки в середине доски — центр, за него сегодня главный бой.', stages: OLD },
    { t: 'Сильный центр даёт простор нашим фигурам и тесноту чужим — это наш план.', stages: OLD },
    { t: 'Играем за центр: чем больше наших фигур смотрит туда, тем лучше.', stages: OLD },
    { t: 'Главное в этой партии — центр: пешки его занимают, а фигуры охраняют.', stages: OLD },
    { t: 'Наш план простой: захватить центр и не отдавать его сопернику.', stages: OLD },
  ],
  'v3.theme.family.counterCenter': [
    { t: 'Сегодня спорим за центр: наши пешки нападают на пешки соперника сбоку.', stages: YOUNG },
    { t: 'Соперник хочет построить центр, а наши пешки будут в него стучаться.', stages: YOUNG },
    { t: 'Наш план — нападать на центр соперника пешками сбоку.', stages: YOUNG },
    { t: 'Поспорим сегодня с соперником, чей будет центр!', stages: YOUNG },
    { t: 'Главное сегодня — смелые пешки: они идут навстречу пешкам соперника в центре.', stages: YOUNG },
    { t: 'Вот эти клетки посередине соперник хочет себе — а мы помешаем!', stages: YOUNG },
    { t: 'Будем как смелые рыцари: нападаем на центр соперника сбоку!', stages: YOUNG },
    { t: 'Тема партии — удар по центру сбоку: не даём сопернику спокойно держать центр.', stages: OLD },
    { t: 'Сегодня бьём по центру соперника сбоку — так мы спорим за него.', stages: OLD },
    { t: 'Наш план — контрудар: пешка сбоку бьёт по центру соперника.', stages: OLD },
    { t: 'Пусть соперник занимает центр — мы ударим по нему сбоку.', stages: OLD },
    { t: 'Большой центр соперника — не беда, а мишень для наших пешек.', stages: OLD },
    { t: 'Вот эти клетки посередине — наша цель: бьём по ним сбоку.', stages: OLD },
    { t: 'Пешки бьют по центру сбоку, а фигуры давят следом — вот наш план.', stages: OLD },
  ],
  'v3.theme.family.development': [
    { t: 'Сегодня главное — быстро разбудить все фигуры и спрятать короля в домик.', stages: YOUNG },
    { t: 'Каждым ходом будим новую фигуру, а потом прячем короля — вот и весь план.', stages: YOUNG },
    { t: 'Наши фигуры пока спят дома — сегодня разбудим их поскорее!', stages: YOUNG },
    { t: 'Устроим гонку: чьи фигуры быстрее выйдут в игру — наши или соперника?', stages: YOUNG },
    { t: 'Главное сегодня — вывести коней и слонов, а потом спрятать короля.', stages: YOUNG },
    { t: 'Играем дружной командой: все фигуры выходят в бой, король — в домик.', stages: YOUNG },
    { t: 'Одной фигурой не гуляем: в бой идёт вся команда!', stages: YOUNG },
    { t: 'Тема партии — развитие: быстро выводим все фигуры и прячем короля.', stages: OLD },
    { t: 'Сегодня обгоняем соперника в развитии: новая фигура каждым ходом, потом рокировка.', stages: OLD },
    { t: 'Наш план — быстрое развитие: кони и слоны в игру, король в безопасность.', stages: OLD },
    { t: 'Главное сегодня — не ходить одной фигурой дважды, пока другие спят.', stages: OLD },
    { t: 'Будем считать фигуры в игре — пусть наших будет больше, чем у соперника.', stages: OLD },
    { t: 'Все фигуры — одна команда: выводим каждую и прячем короля пораньше.', stages: OLD },
    { t: 'Лёгкие фигуры выходят первыми, потом рокировка, а за ней и ладьи.', stages: OLD },
  ],
  'v3.theme.family.castle': [
    { t: 'Сегодня главное — рокировка: король прячется в домик, а ладья выходит в игру.', stages: YOUNG },
    { t: 'Королю в центре опасно — сегодня спрячем его в домик пораньше.', stages: YOUNG },
    { t: 'План на партию: выводим фигуры, освобождаем дорогу и делаем рокировку.', stages: YOUNG },
    { t: 'Вот наш король — сегодня мы пораньше построим ему надёжный домик.', stages: YOUNG },
    { t: 'Король у нас один, поэтому сегодня первым делом думаем о его безопасности.', stages: YOUNG },
    { t: 'Коню и слону пора уйти с дороги — тогда король сможет спрятаться.', stages: YOUNG },
    { t: 'Чем раньше рокировка, тем спокойнее игра — так сегодня и сыграем.', stages: YOUNG },
    { t: 'Тема партии — король в крепости: делаем рокировку пораньше.', stages: OLD },
    { t: 'Сегодня наш план — безопасность короля: быстрая рокировка, а потом игра.', stages: OLD },
    { t: 'Пока король в центре, до него легко добраться — уведём его в крепость заранее.', stages: OLD },
    { t: 'Главное в этой партии — крепость для короля: рокировка и пешки перед ним.', stages: OLD },
    { t: 'Когда король в крепости, можно смело думать об атаке — в этом наш план.', stages: OLD },
    { t: 'Вот наш король — спрячем его в крепость, пока линии не открылись.', stages: OLD },
    { t: 'Строим королю крепость: рокировка, а перед ним — надёжные пешки.', stages: OLD },
  ],
  'v3.theme.family.f7': [
    { t: 'Быстро выводим фигуры и целимся в пешку рядом с королём соперника!', stages: YOUNG },
    { t: 'Пешку рядом с чужим королём защищает только король — в неё мы и целимся.', stages: YOUNG },
    { t: 'Разбудим фигуры и направим их на пешку у короля соперника.', stages: YOUNG },
    { t: 'Вот эта пешка у чужого короля — сегодня наши фигуры будут на неё смотреть.', stages: YOUNG },
    { t: 'Главное сегодня — быстрые фигуры и прицел на пешку возле короля соперника.', stages: YOUNG },
    { t: 'Будем охотниками: наши фигуры выходят и смотрят на пешку у короля соперника.', stages: YOUNG },
    { t: 'Сегодня целимся в слабую пешку у короля соперника — её защищает только король.', stages: [2, 2] },
    { t: 'Тема партии — слабая пешка у короля соперника: быстро выводим фигуры и целимся туда.', stages: OLD },
    { t: 'Сегодня наведём слона и коня на слабую пешку возле чужого короля.', stages: OLD },
    { t: 'В начале партии у соперника есть слабая пешка у короля — туда и целимся.', stages: OLD },
    { t: 'Наш план — быстрое развитие и давление на слабую пешку у короля соперника.', stages: OLD },
    { t: 'Вот эта пешка защищена только королём — сегодня наши фигуры целятся в неё.', stages: OLD },
    { t: 'Фигуры выходят быстро и все вместе давят на слабую пешку у чужого короля.', stages: OLD },
    { t: 'Играем на скорость: наши фигуры спешат к слабой пешке у короля соперника.', stages: OLD },
  ],
  'v3.theme.family.fortress': [
    { t: 'Сегодня строим из пешек крепость в центре, а за ней спокойно выводим фигуры.', stages: YOUNG },
    { t: 'Наши пешки встанут в центре крепкой стеной — их трудно сломать.', stages: YOUNG },
    { t: 'Пешки встанут рядом и будут защищать друг друга, как верные друзья.', stages: YOUNG },
    { t: 'Никуда не спешим: ставим пешки стеной и спокойно выводим фигуры.', stages: YOUNG },
    { t: 'Вот эти клетки посередине — здесь мы построим крепость из пешек.', stages: YOUNG },
    { t: 'Главное сегодня — играть надёжно: пусть сопернику негде будет пробраться.', stages: YOUNG },
    { t: 'Играем спокойно и крепко: пешки держат центр, а фигуры им помогают.', stages: YOUNG },
    { t: 'Тема партии — крепость: пешки держат центр, а фигуры спокойно выходят под их защитой.', stages: OLD },
    { t: 'Сегодня строим надёжный центр из пешек, который сопернику трудно сломать.', stages: OLD },
    { t: 'Наш план — крепкая цепочка пешек в центре и спокойное развитие за ней.', stages: OLD },
    { t: 'Вот эти клетки в центре мы займём пешками и никому не отдадим.', stages: OLD },
    { t: 'Атака подождёт: сперва пешки в центре должны встать надёжно.', stages: OLD },
    { t: 'Главное в этой партии — не спешить, а крепко стоять в центре и выводить фигуры.', stages: OLD },
    { t: 'Строим крепость из пешек, а когда фигуры выйдут, готовим удар в центре.', stages: OLD },
  ],
  'v3.theme.family.gambit': [
    { t: 'Сегодня предложим сопернику пешку, а за это заберём центр.', stages: YOUNG },
    { t: 'Наш план хитрый: предложить пешку, чтобы потом забрать центр.', stages: YOUNG },
    { t: 'Иногда пешку можно отдать, если за неё получаешь центр, — так сыграем сегодня.', stages: YOUNG },
    { t: 'Пешка будет приманкой: возьмёт её соперник — наши фигуры выйдут быстрее.', stages: YOUNG },
    { t: 'Главное сегодня — центр: ради него мы готовы отдать пешку.', stages: YOUNG },
    { t: 'Вот эти клетки посередине — центр, за него мы и предложим пешку.', stages: YOUNG },
    { t: 'Смелый план на партию: пешку предлагаем, а центр забираем себе.', stages: YOUNG },
    { t: 'Тема партии — гамбит: предлагаем пешку, чтобы забрать центр и быстрее вывести фигуры.', stages: OLD },
    { t: 'Гамбит — это когда пешку отдают за центр и быстрые фигуры, — так и сыграем.', stages: OLD },
    { t: 'Сегодня не жадничаем: пешка — приманка, а наша цель — центр.', stages: OLD },
    { t: 'Наш план — открыть линии и занять центр, даже если придётся отдать пешку.', stages: OLD },
    { t: 'Вот эти клетки посередине стоят дороже пешки — за них и поборемся.', stages: OLD },
    { t: 'Главное в этой партии — быстрые фигуры и центр, даже ценой пешки.', stages: OLD },
    { t: 'Пешка за центр — хороший обмен, если фигуры успеют выйти быстрее.', stages: OLD },
  ],
  'v3.theme.family.openFile': [
    { t: 'Сегодня ищем ладьям свободные дороги, где нет пешек.', stages: YOUNG },
    { t: 'Ладьи любят простор: поставим их туда, где пешки не мешают.', stages: YOUNG },
    { t: 'Наш план: вывести фигуры, спрятать короля и отправить ладьи на свободные дороги.', stages: YOUNG },
    { t: 'Главное сегодня — чтобы ладьи не стояли без дела, а бежали по свободным дорогам.', stages: YOUNG },
    { t: 'Ладья хорошо работает там, где нет пешек, — туда её и поставим.', stages: YOUNG },
    { t: 'Наши главные бегуны сегодня — ладьи: откроем им дорогу через всю доску.', stages: YOUNG },
    { t: 'Будим все фигуры, а потом отправляем ладьи туда, где свободно.', stages: YOUNG },
    { t: 'Тема партии — открытые линии: ставим ладьи туда, где нет пешек.', stages: OLD },
    { t: 'Сегодня наши ладьи ищут открытые линии и давят по ним вперёд.', stages: OLD },
    { t: 'Наш план — открыть линию и поставить на неё ладью.', stages: OLD },
    { t: 'Ладья на открытой вертикали достаёт до самого края доски — такие линии и ищем.', stages: OLD },
    { t: 'Главное в этой партии — ладьи: выводим их на открытые линии и соединяем.', stages: OLD },
    { t: 'Две ладьи на одной открытой линии — вот наша цель на эту партию.', stages: OLD },
    { t: 'Сначала фигуры и рокировка, а потом ладьи выходят на открытые линии.', stages: OLD },
  ],
  'v3.theme.family.kingsideAttack': [
    { t: 'Сегодня готовим атаку на короля соперника!', stages: YOUNG },
    { t: 'Наш план: вывести фигуры, спрятать своего короля, а потом идти на чужого.', stages: YOUNG },
    { t: 'Главное сегодня — собрать фигуры поближе к королю соперника и напасть.', stages: YOUNG },
    { t: 'Сначала крепость для нашего короля, потом наступление на короля соперника.', stages: YOUNG },
    { t: 'Играем в нападение: наши фигуры и пешки идут к чужому королю.', stages: YOUNG },
    { t: 'Мы сегодня нападаем, а король соперника — наша главная цель.', stages: YOUNG },
    { t: 'На стороне короля соперника будет жарко — туда мы и поведём фигуры.', stages: YOUNG },
    { t: 'Тема партии — атака на короля: собираем фигуры на королевском фланге.', stages: OLD },
    { t: 'Сегодня наш план — атака: пешки и фигуры идут на короля соперника.', stages: OLD },
    { t: 'Главное в этой партии — атака на королевском фланге, пока наш король в безопасности.', stages: OLD },
    { t: 'Сначала прячем своего короля, а потом ведём пешки и фигуры в атаку на чужого.', stages: OLD },
    { t: 'Наш план — открыть линии к королю соперника и привести туда фигуры.', stages: OLD },
    { t: 'Атакуем короля: одной фигуры мало, нужна целая команда.', stages: OLD },
    { t: 'Король соперника обычно прячется на королевском фланге — туда и направим удар.', stages: OLD },
  ],
  'v3.theme.family.queensideAttack': [
    { t: 'Сегодня главные события будут на стороне ферзя.', stages: YOUNG },
    { t: 'Наш план — шаг за шагом двигать пешки на стороне ферзя.', stages: YOUNG },
    { t: 'Главное сегодня — сторона ферзя: пешки идут вперёд, фигуры им помогают.', stages: YOUNG },
    { t: 'Мы будем наступать сбоку — со стороны ферзя, пешками и фигурами.', stages: YOUNG },
    { t: 'Пешки на стороне ферзя сегодня — наши разведчики: они смело идут вперёд.', stages: YOUNG },
    { t: 'Сначала выводим фигуры, потом двигаем пешки на стороне ферзя.', stages: YOUNG },
    { t: 'Будем забирать у соперника место на стороне ферзя.', stages: YOUNG },
    { t: 'Тема партии — ферзевый фланг: наступаем там пешками и фигурами.', stages: OLD },
    { t: 'Сегодня играем на ферзевом фланге: пешки забирают место, ладьи и слоны помогают.', stages: OLD },
    { t: 'Наш план — наступление пешками на ферзевом фланге, а фигуры идут следом.', stages: OLD },
    { t: 'Главное в этой партии — ферзевый фланг: там мы забираем место у соперника.', stages: OLD },
    { t: 'Двигаем пешки на ферзевом фланге — сопернику там станет тесно.', stages: OLD },
    { t: 'Сначала фигуры и рокировка, потом наступление на ферзевом фланге.', stages: OLD },
    { t: 'Ладьи и пешки вместе давят на ферзевом фланге — это наш план.', stages: OLD },
  ],

  // ───────────────────────── v3.theme.remind.<family>: the plan again, never «the goal is (not) reached» ─────────────
  // A reminder comes after 4+ quiet turns in the opening OR while a card goal is open — so also deep in the middlegame
  // and even in the endgame, glued in front of an advice. It states the plan or a norm, never the position («наша
  // крепость держится», «фигуры выходят», «каждый ход — новая фигура», «ещё спит» are false there), and never asks for
  // a delay («удар подождёт») that the advice right after it may contradict.
  'v3.theme.remind.center': [
    { t: 'Помним наш план: центр должен достаться нам.' },
    { t: 'Вспомним тему партии — боремся за центр пешками и фигурами.' },
    { t: 'Наша тема всё та же — центр.' },
    { t: 'Каждый ход спрашиваем себя: помогает ли он центру?' },
    { t: 'О центре не забываем: он нужен и пешкам, и фигурам.' },
    { t: 'Вот они, клетки центра, — продолжаем за них бороться.' },
    { t: 'Продолжаем наш план: пусть побольше пешек и фигур смотрит в центр.' },
    { t: 'Напомню тему: центр — сердце доски, и он нужен нам.', stages: OLD },
  ],
  'v3.theme.remind.counterCenter': [
    { t: 'Помним наш план: бьём по центру соперника сбоку.' },
    { t: 'Вспомним тему — центр соперника не должен стоять спокойно.' },
    { t: 'Наша цель прежняя — центр соперника.' },
    { t: 'Если у соперника пешки в центре — ищем, чем по ним ударить.' },
    { t: 'Продолжаем спорить за центр — пешками сбоку и фигурами.' },
    { t: 'Пешки сбоку — наше главное оружие против центра соперника.' },
    { t: 'Главное по-прежнему — спор за центр.' },
    { t: 'Напомню тему: контрудар сбоку по центру соперника.', stages: OLD },
  ],
  'v3.theme.remind.development': [
    { t: 'Помним наш план: сильна та команда, где играют все фигуры.' },
    { t: 'Вспомним тему: все ли наши фигуры уже в игре?' },
    { t: 'Наша тема — вся наша команда должна выйти в игру.' },
    { t: 'Каждой фигуре — своё поле, где она полезнее всего.' },
    { t: 'Если какая-то наша фигура ещё спит дома — будим её.' },
    { t: 'Продолжаем наш план: фигуры помогают друг другу, никто не воюет в одиночку.' },
    { t: 'Главное по-прежнему — фигуры в деле, а король в безопасности.' },
    { t: 'Напомню тему — развитие: после коней и слонов очередь ладей.', stages: OLD },
  ],
  'v3.theme.remind.castle': [
    { t: 'Помним наш план: король должен жить в безопасном домике.' },
    { t: 'Вспомним тему — королю нужны крепкие стены вокруг.' },
    { t: 'Наша тема — безопасность короля, это главное.' },
    { t: 'Прежде чем нападать, проверим: наш король в безопасности?' },
    { t: 'Бережём короля — без него и игра не игра.' },
    { t: 'Продолжаем план: чем надёжнее домик короля, тем смелее игра.' },
    { t: 'Главное по-прежнему — чтобы к нашему королю было трудно подобраться.' },
    { t: 'Напомню тему: пешки перед нашим королём без нужды не двигаем.', stages: OLD },
  ],
  'v3.theme.remind.f7': [
    { t: 'Помним наш план: целимся в пешку рядом с королём соперника.' },
    { t: 'Вспомним тему: наши фигуры целятся в сторону короля соперника.' },
    { t: 'Наша цель прежняя — пешка возле чужого короля.' },
    { t: 'Продолжаем охоту: какая фигура ещё может посмотреть на пешку у короля соперника?' },
    { t: 'Главное по-прежнему — не терять из виду пешку у короля соперника.' },
    { t: 'Не отвлекаемся от охоты за пешкой у короля соперника.' },
    { t: 'Чем больше наших фигур смотрит на пешку у короля соперника, тем лучше.' },
    { t: 'Напомню тему: слабая пешка у короля соперника — наша цель.', stages: FROM2 },
  ],
  'v3.theme.remind.fortress': [
    { t: 'Помним наш план: крепость из пешек в центре.' },
    { t: 'Вспомним тему — пешки в центре стоят плечом к плечу.' },
    { t: 'Крепость сильна, пока соперник не нашёл в ней слабого места.' },
    { t: 'Наш центр — как стена: пусть в ней не будет дыр.' },
    { t: 'Следим, чтобы ни одна пешка в центре не осталась без защиты.' },
    { t: 'Главное по-прежнему — надёжный центр и спокойная игра.' },
    { t: 'Каждый ход проверяем: крепка ли наша крепость?' },
    { t: 'Напомню тему: на прочных пешках держится вся позиция.', stages: OLD },
  ],
  'v3.theme.remind.gambit': [
    { t: 'Помним наш план: главное — центр и быстрые фигуры, а не пешка.' },
    { t: 'Вспомним тему — за пешку мы хотим получить центр.' },
    { t: 'Наша цель прежняя — центр и фигуры в игре.' },
    { t: 'Не жадничаем: сначала фигуры и центр, пешки потом.' },
    { t: 'Продолжаем план: быстрые фигуры и центр — вот что нам нужно.' },
    { t: 'Главное по-прежнему — наш центр и наши фигуры.' },
    { t: 'Наши фигуры должны работать лучше, чем у соперника, — в этом наш план.' },
    { t: 'Напомню тему: гамбит — это центр и быстрые фигуры за одну пешку.', stages: OLD },
  ],
  'v3.theme.remind.openFile': [
    { t: 'Помним наш план: ладьям нужны свободные дороги.' },
    { t: 'Вспомним тему — где ладье свободнее бежать?' },
    { t: 'Наша цель прежняя — ладьи на дорогах без пешек.' },
    { t: 'Ладьи тоже хотят в бой — продолжаем наш план.' },
    { t: 'Продолжаем: ищем для ладьи дорогу, где пешки не мешают.' },
    { t: 'Главное по-прежнему — наши ладьи.' },
    { t: 'Каждая ладья ждёт свою свободную дорогу.' },
    { t: 'Напомню тему: ладья на открытой линии давит до самого конца доски.', stages: OLD },
  ],
  'v3.theme.remind.kingsideAttack': [
    { t: 'Помним наш план: готовим атаку на короля соперника.' },
    { t: 'Вспомним тему — атакуем короля соперника всей командой.' },
    { t: 'Наша цель прежняя — король соперника.' },
    { t: 'Продолжаем: какая фигура ещё может подобраться к королю соперника?' },
    { t: 'Сначала наш король в безопасности, потом атака — таков наш план.' },
    { t: 'Главное по-прежнему — побольше наших фигур рядом с чужим королём.' },
    { t: 'Зовём на помощь фигуры, которые ещё далеко от короля соперника.' },
    { t: 'Напомню тему: атакуем на королевском фланге пешками и фигурами вместе.', stages: OLD },
  ],
  'v3.theme.remind.queensideAttack': [
    { t: 'Помним наш план: главная игра — на стороне ферзя.' },
    { t: 'Вспомним тему — двигаем пешки на стороне ферзя.' },
    { t: 'Наша цель прежняя — место на стороне ферзя.' },
    { t: 'Продолжаем: какая пешка на стороне ферзя может шагнуть вперёд?' },
    { t: 'Главное по-прежнему — наступать сбоку, со стороны ферзя.' },
    { t: 'Сторона ферзя ждёт наших пешек и фигур.' },
    { t: 'Шаг за шагом забираем место на стороне ферзя.' },
    { t: 'Напомню тему: наступаем на ферзевом фланге пешками и ладьями.', stages: OLD },
  ],

  // ───────────────────────── v3.theme.left: the opponent left the card's line (stages 3–5) ─────────────────────────
  'v3.theme.left': [
    { t: 'Соперник свернул с нашей дорожки — ничего, план остаётся тем же.' },
    { t: 'Пусть соперник идёт по-своему — наша тема не меняется.' },
    { t: 'Ходы теперь другие, а цель та же — продолжаем наш план.' },
    { t: 'Дебют пошёл иначе, чем в книжке, — не беда, идеи те же.' },
    { t: 'Так-так, соперник придумал своё — а мы держимся нашего плана.' },
    { t: 'Дорога изменилась, но мы идём к той же цели.' },
    { t: 'Соперник сошёл с привычной линии — значит, думаем сами, но по нашему плану.' },
    { t: 'Ничего страшного, что ходы другие: план тот же, просто путь новый.' },
    { t: 'Это уже новые ходы, но наша тема никуда не делась.' },
    { t: 'Новые ходы — новая задачка, а цель у нас прежняя.' },
  ],

  // ───────────────────────── chapters of the game ─────────────────────────
  // The middlegame comes after move 10 (15 with minors at home) or after early trades — some pieces may still be at
  // home, so no «фигуры вышли / развиты». The endgame comes at ≤ 26 pawns of pieces (≤ 32 without queens) — a queen
  // or four pieces may still be on the board, so the king «может» fight, never «смело идёт».
  'v3.phase.middlegame': [
    { t: 'Первые ходы сделаны — теперь ищем, кого атаковать.' },
    { t: 'Начало партии позади: пора искать, где у соперника слабое место.' },
    { t: 'Начинается середина партии — пора придумывать атаки.' },
    { t: 'Разминка закончилась: теперь каждая фигура ищет себе работу.' },
    { t: 'Теперь самое интересное: ищем, какая фигура соперника стоит плохо.' },
    { t: 'Середина партии: ищем фигуры соперника без защиты и думаем о плане.' },
    { t: 'Вот и настоящая битва — думаем, что задумал соперник и что можем мы.' },
    { t: 'Дебют закончен — теперь ищем цели для атаки и строим план.', stages: OLD },
    { t: 'Пора строить план: пусть каждый наш ход теперь работает на него.', stages: OLD },
  ],
  'v3.phase.endgame': [
    { t: 'Фигур осталось мало — теперь и король может вступить в бой!' },
    { t: 'Доска пустеет — значит, и король может стать бойцом.' },
    { t: 'Началась последняя часть партии — пешки мечтают стать ферзями.' },
    { t: 'Пешкам теперь нужен король рядом — вместе они сильнее.' },
    { t: 'Пора подумать о пешках: какую из них можно повести к превращению?' },
    { t: 'Последняя часть партии: каждая пешка теперь очень важна.' },
    { t: 'Королю пора в путь — когда фигур мало, ему уже не так опасно.' },
    { t: 'Начинается эндшпиль: королю пора ближе к центру, а пешкам — вперёд.', stages: ONLY5 },
    { t: 'Эндшпиль: ищем проходную пешку и ведём её вперёд вместе с королём.', stages: ONLY5 },
  ],

  // ───────────────────────── v3.recall.<takeaway>: a recent lesson, checked today (time-neutral) ─────────────────────────
  // Said at the start, right after the announcement (before our first move); the takeaway may be of the last game or
  // of the one before. The frames vary between the pools, so five recalls in ten games do not sound alike.
  'v3.recall.mistake.mate': [
    { t: 'Помнишь, недавно мы учились смотреть, что задумал соперник, — сегодня проверим!' },
    { t: 'Есть у нас правило: сначала смотрим, что задумал соперник, а потом ходим сами.' },
    { t: 'Сегодня перед каждым ходом глядим, нет ли у соперника мата, — это наше правило.' },
    { t: 'Вспомним наш урок — сегодня постараемся заметить угрозу мата заранее.' },
    { t: 'Я запомнил наш урок про мат — сегодня вместе следим за нашим королём.' },
  ],
  'v3.recall.mistake.backRank': [
    { t: 'Помнишь про форточку для короля — сегодня откроем её вовремя!' },
    { t: 'Недавно мы узнали: королю нужна форточка — сегодня посмотрим, как у нас получится.' },
    { t: 'Сегодня проверим наш урок: король не должен сидеть запертым в последнем ряду.' },
    { t: 'Держим в голове наш урок: король не должен оказаться заперт своими пешками.' },
    { t: 'Я запомнил наш урок про последний ряд — сегодня проверим его вместе.' },
  ],
  'v3.recall.mistake.fork': [
    { t: 'Помнишь, недавно соперник напал сразу на две наши фигуры — сегодня будем начеку!' },
    { t: 'Правило на сегодня: смотрим, куда может прыгнуть конь соперника.' },
    { t: 'Недавно мы учились замечать двойной удар — сегодня поищем его заранее.' },
    { t: 'Вспомним наш урок — сегодня следим, чтобы никто не напал сразу на две наши фигуры.' },
    { t: 'Я запомнил ту вилку соперника — сегодня будем замечать вилки вовремя!', stages: FROM2 },
  ],
  'v3.recall.mistake.pin': [
    { t: 'Сегодня проверим наш урок: не ставим фигуры на одну линию со своим королём.' },
    { t: 'Помнишь, недавно нашей фигуре нельзя было уйти — сегодня будем следить за линиями.' },
    { t: 'Недавно мы узнали: фигуре перед нашим королём иногда нельзя уйти — помним об этом.' },
    { t: 'Вспомним наш урок — сегодня смотрим, кто стоит на одной линии с нашим королём.' },
    { t: 'Я запомнил ту связку — сегодня первыми посмотрим на все линии к нашему королю.', stages: OLD },
  ],
  'v3.recall.mistake.hanging': [
    { t: 'Помнишь, недавно мы учились защищать фигуры — сегодня проверим!' },
    { t: 'Держим в голове: перед каждым ходом смотрим, всё ли у нас защищено.' },
    { t: 'Недавно мы узнали: фигура без защиты — лёгкая добыча, так что сегодня бережём каждую.' },
    { t: 'Вспомним наш урок — сегодня следим, чтобы каждая фигура была под защитой.' },
    { t: 'Я запомнил наше правило: все фигуры под защитой — посмотрим, как сегодня получится!' },
  ],
  'v3.recall.mistake.badTrade': [
    { t: 'Помнишь, недавно мы учились считать размены — сегодня проверим!' },
    { t: 'Сегодня проверим наш урок: меняемся, только если получаем не меньше.' },
    { t: 'Недавно мы узнали: перед разменом считаем, что отдаём и что забираем, — сегодня посчитаем вместе.' },
    { t: 'Есть у нас правило: сначала считаем, потом меняемся, — сегодня проверим его.' },
    { t: 'Я запомнил наш урок про размены — сегодня будем меняться только выгодно.' },
  ],
  'v3.recall.mistake.ignoredDanger': [
    { t: 'Помнишь, недавно мы учились сначала спасать того, на кого напали, — сегодня проверим!' },
    { t: 'Сегодня проверим наш урок: на кого напали, того и выручаем первым.' },
    { t: 'Недавно мы узнали: на угрозу сначала отвечаем, а потом играем дальше, — так и сыграем.' },
    { t: 'Вспомним наш урок — сегодня каждую угрозу встречаем сразу.' },
    { t: 'Я запомнил наше правило: сначала выручаем того, кого атакуют, — посмотрим сегодня!' },
  ],
  'v3.recall.mistake.missedTreasure': [
    { t: 'Помнишь, недавно мы учились искать подарки — сегодня проверим!' },
    { t: 'Правило на сегодня: сначала ищем, что можно съесть бесплатно.' },
    { t: 'Недавно мы узнали: подарки на доске надо искать каждый ход — сегодня поищем вместе.' },
    { t: 'Вспомним наш урок — сегодня смотрим в оба, нет ли где подарка.' },
    { t: 'Я запомнил наш урок про подарки — сегодня перед каждым ходом будем их искать.' },
  ],
  'v3.recall.mistake.earlyQueen': [
    { t: 'Сегодня проверим наше правило: сначала кони и слоны, а ферзь потом.' },
    { t: 'Помнишь, недавно мы учились беречь ферзя — сегодня выведем его попозже.' },
    { t: 'Недавно мы узнали: рано выведенного ферзя гоняют — сегодня пусть он подождёт.' },
    { t: 'Вспомним наш урок — сегодня не спешим выводить ферзя.' },
    { t: 'Я запомнил урок про ранний выход ферзя — сегодня сначала будим коней и слонов.', stages: FROM2 },
  ],
  'v3.recall.mistake.tactic': [
    { t: 'Помнишь, недавно соперник нашёл хитрый удар — сегодня будем следить за линиями!' },
    { t: 'Сегодня проверим наш урок: смотрим, что откроется, если фигура уйдёт.' },
    { t: 'Недавно мы узнали: хитрые удары прячутся на линиях — сегодня поищем их первыми.' },
    { t: 'Вспомним наш урок — сегодня перед ходом оглядываем все линии, прямые и косые.' },
    { t: 'Я запомнил тот хитрый удар соперника — сегодня проверим линии и диагонали заранее.', stages: OLD },
  ],
  'v3.recall.theme.center': [
    { t: 'Помнишь, недавно у нас хорошо получилось держать центр — сегодня попробуем ещё раз!' },
    { t: 'Сегодня проверим наш урок про центр: чем больше там наших фигур, тем нам легче.' },
    { t: 'Недавно наш центр стоял крепко — посмотрим, получится ли так и сегодня.' },
    { t: 'Мне понравилось, как мы недавно играли за центр, — сыграем так же?' },
    { t: 'Я запомнил, как здорово мы держали центр, — повторим сегодня?' },
  ],
  'v3.recall.theme.counterCenter': [
    { t: 'Здорово у нас недавно получился удар по центру сбоку — попробуем повторить!' },
    { t: 'Недавний урок: за центр можно спорить и сбоку — посмотрим, пригодится ли сегодня.' },
    { t: 'Помнишь, как недавно мы спорили за центр, — посмотрим, как выйдет сегодня.' },
    { t: 'Вспомним, как наша пешка била по центру сбоку, — поищем такой удар и сегодня.' },
    { t: 'Я запомнил наш смелый удар по центру — вдруг сегодня получится ещё лучше?' },
  ],
  'v3.recall.theme.development': [
    { t: 'Мне понравилось, как быстро мы недавно будили фигуры, — повторим?' },
    { t: 'Сегодня проверим наш урок: каждым ходом будим новую фигуру.' },
    { t: 'Недавно наши фигуры дружно вышли в бой — посмотрим, получится ли сегодня.' },
    { t: 'Вспомним наш порядок: фигуры в игру, король в домик — повторим сегодня!' },
    { t: 'Я запомнил наше быстрое развитие — сегодня попробуем ещё быстрее!', stages: FROM2 },
  ],
  'v3.recall.theme.castle': [
    { t: 'Помнишь, недавно наш король вовремя спрятался в домик — сегодня проверим ещё раз!' },
    { t: 'Правило из нашего урока: короля прячем пораньше.' },
    { t: 'Недавно наш король жил в надёжной крепости — построим такую же сегодня.' },
    { t: 'Вспомним, как рокировка недавно получилась вовремя, — сегодня тоже не опоздаем!' },
    { t: 'Я запомнил, как надёжно мы спрятали короля, — сделаем так же?' },
  ],
  'v3.recall.theme.f7': [
    { t: 'Помнишь, недавно наши фигуры целились в пешку у короля соперника — сегодня проверим!' },
    { t: 'Прицел на пешку у короля соперника недавно сработал — пригодится ли он сегодня?' },
    { t: 'Недавно мы хорошо прицелились в пешку у чужого короля — поохотимся и сегодня?' },
    { t: 'Вспомним, как наши фигуры смотрели на короля соперника, — поищем такой шанс сегодня.' },
    { t: 'Я запомнил, как мы давили на слабую пешку, — сегодня попробуем ещё!', stages: FROM2 },
  ],
  'v3.recall.theme.fortress': [
    { t: 'Помнишь, недавно наша крепость из пешек стояла крепко — сегодня проверим ещё раз!' },
    { t: 'Недавний урок: пешки, которые защищают друг друга, держатся крепко, — проверим сегодня.' },
    { t: 'Недавно наша стена из пешек выстояла — посмотрим, пригодится ли она сегодня.' },
    { t: 'Вспомним, как наши пешки держали центр, — сегодня тоже стоим крепко.' },
    { t: 'Я запомнил нашу крепость из пешек — посмотрим, построим ли такую сегодня.' },
  ],
  'v3.recall.theme.gambit': [
    { t: 'Помнишь, недавно мы предложили пешку ради центра — сегодня попробуем ещё раз!' },
    { t: 'Сегодня проверим наш урок: за пешку можно получить центр и быстрые фигуры.' },
    { t: 'Недавно наша пешка-приманка сработала — посмотрим, как выйдет сегодня.' },
    { t: 'Вспомним наш смелый план с пешкой — сегодня поищем такой шанс.' },
    { t: 'Я запомнил, как смело мы играли за центр, не жалея пешки, — повторим?' },
  ],
  'v3.recall.theme.openFile': [
    { t: 'Мне понравилось, как недавно бегали наши ладьи, — повторим сегодня?' },
    { t: 'Держим в голове: ладьям нужны дороги без пешек.' },
    { t: 'Недавно наши ладьи хорошо поработали — посмотрим, найдут ли они дорогу сегодня.' },
    { t: 'Вспомним наш урок: ладья на свободной дороге очень сильна — сегодня поищем такую дорогу.' },
    { t: 'Я запомнил наши ладьи на открытых линиях — сегодня так же?', stages: OLD },
  ],
  'v3.recall.theme.kingsideAttack': [
    { t: 'Помнишь, недавно наши фигуры дружно пошли на короля соперника — сегодня проверим!' },
    { t: 'Есть у нас правило: сначала свой король в домике, потом атака.' },
    { t: 'Недавно наша атака на короля получилась — попробуем и сегодня.' },
    { t: 'Вспомним, как мы собрали фигуры у чужого короля, — посмотрим, выйдет ли сегодня.' },
    { t: 'Я запомнил нашу недавнюю атаку — сегодня поищем, как её повторить.' },
  ],
  'v3.recall.theme.queensideAttack': [
    { t: 'Мне понравилось, как наши пешки недавно шли вперёд на стороне ферзя, — повторим?' },
    { t: 'Сегодня проверим наш урок: сторона ферзя — тоже поле для атаки.' },
    { t: 'Недавно мы хорошо сыграли на стороне ферзя — посмотрим, получится ли сегодня.' },
    { t: 'Вспомним, как пешки на стороне ферзя забирали место, — поищем такую игру и сегодня.' },
    { t: 'Я запомнил нашу игру на ферзевом фланге — сегодня поищем такую же!', stages: OLD },
  ],
};
